// t(key, params): the one way the surface turns a catalogue key into words. No i18n framework yet;
// the catalogue exports to flat JSON (toFlatJson) so one can be adopted later without rewriting it.
//
// Keys and parameters are checked by the compiler: an unknown key, a missing parameter or a stray
// one is a type error. At run time the same mistakes throw, so a test that reaches one fails loudly
// instead of rendering a raw key or an empty slot.
import { en } from "./en.js";
const PLACEHOLDER = /\{(\w+)\}/g;
// Plural categories come from English rules for both locales: the pseudo-locale is English in disguise.
const pluralRules = new Intl.PluralRules("en");
// prettier-ignore
const PSEUDO = {
    a: "á", e: "é", i: "í", o: "ó", u: "ú", y: "ý", c: "ç", n: "ñ", s: "š", z: "ž",
    A: "Á", E: "É", I: "Í", O: "Ó", U: "Ú", Y: "Ý", C: "Ç", N: "Ñ", S: "Š", Z: "Ž",
};
/** Accents the letters outside `{placeholders}` and brackets the result, so untranslated text shows. */
function pseudoize(template) {
    const body = template
        .split(/(\{\w+\})/)
        .map((part) => /^\{\w+\}$/.test(part) ? part : part.replace(/[a-zA-Z]/g, (c) => PSEUDO[c] ?? c))
        .join("");
    return `[${body}]`;
}
function hasKey(key) {
    return Object.hasOwn(en, key);
}
function pick(key, value, params) {
    if (typeof value === "string")
        return value;
    const count = params["count"];
    if (typeof count !== "number")
        throw new Error(`t("${key}"): the parameter "count" must be a number to choose a plural form`);
    return value[pluralRules.select(count)] ?? value.other;
}
function fill(key, template, params) {
    return template.replace(PLACEHOLDER, (_match, name) => {
        const value = params[name];
        if (typeof value !== "string" && typeof value !== "number")
            throw new Error(`t("${key}"): missing parameter "${name}"`);
        return String(value);
    });
}
/** A t() bound to one locale. The surface uses `t` (English); tests build the pseudo one here. */
export function translator(locale) {
    return (key, ...args) => {
        // The compiler already rejects an unknown key; this guards callers that bypass it (a cast, JS).
        if (!hasKey(key))
            throw new Error(`t(): unknown key "${String(key)}"`);
        const params = args[0] ?? {};
        const template = pick(key, en[key], params);
        return fill(key, locale === "pseudo" ? pseudoize(template) : template, params);
    };
}
export const t = translator("en");
/** Every key of the catalogue, in catalogue order. */
export const messageKeys = Object.keys(en);
/**
 * The catalogue as flat JSON: one string per key. A plural message becomes one entry per form,
 * suffixed `_one`, `_other` and so on, which is the i18next v4 JSON layout an adopted framework
 * can read. Placeholders stay `{name}`.
 */
export function toFlatJson(locale = "en") {
    const out = {};
    const render = locale === "pseudo" ? pseudoize : (s) => s;
    for (const [key, value] of Object.entries(en)) {
        if (typeof value === "string")
            out[key] = render(value);
        else
            for (const [form, text] of Object.entries(value))
                out[`${key}_${form}`] = render(text);
    }
    return out;
}
//# sourceMappingURL=t.js.map